import { Button, Modal } from "semantic-ui-react";
import { CertificateType, ICertificate } from "../../models/certificate";
import { IIDDataSet } from "../../models/crewMember";
import { BoatMasterATCertificateView } from "./CertificateViews/BoatMasterATCertificateView";
import { InternationalCertificateView } from "./CertificateViews/InternationalCertificateView";
import { SailingOnWaterwaysCertificateView } from "./CertificateViews/SailingOnWaterwaysCertificateView";
import { EuropeanCertificateView } from "./CertificateViews/EuropeanCertificateView";
import { useIntl } from "react-intl";

type Props = {
    open: boolean;
    close: () => void;
    certificate: ICertificate;
    idDataSet: IIDDataSet;
    renew?: boolean;
    actions?: boolean;
    handleAddCertificate?: () => Promise<void>;
    handleRenewCertificate?: () => Promise<void>;
    handleUpdateCertificate?: () => Promise<void>;
    isUpdate?: boolean;
    loading?: boolean;
    readonly?: boolean;
    qrBase64?: string;
};

export const ViewCertificateModal = ({
    open,
    close,
    certificate,
    idDataSet,
    renew,
    actions,
    handleAddCertificate,
    handleRenewCertificate,
    handleUpdateCertificate,
    loading,
    isUpdate = false,
    readonly = false,
    qrBase64,
}: Props) => {
    const intl = useIntl();
    const modifyable =
        actions !== undefined &&
        handleAddCertificate !== undefined &&
        handleUpdateCertificate !== undefined &&
        handleRenewCertificate !== undefined;

    return (
        <Modal
            open={open}
            onClose={() => close()}
            size="small"
            className="certificate-view-modal"
            dimmer="inverted"
            closeOnDimmerClick={!actions}
        >
            <Modal.Content className="certificate-view">
                {(() => {
                    switch (certificate.type) {
                        case CertificateType.UcqBoatmaster:
                        case CertificateType.UcqLngExpert:
                        case CertificateType.UcqPassengerNavigationExpert:
                            return (
                                <EuropeanCertificateView
                                    certificate={certificate}
                                    idDataSet={idDataSet}
                                    modifyable={modifyable}
                                    qrBase64={qrBase64 ?? ""}
                                />
                            );
                        case CertificateType.BoatmastersCertificateAt:
                            return (
                                <BoatMasterATCertificateView
                                    certificate={certificate}
                                    idDataSet={idDataSet}
                                    modifyable={modifyable}
                                />
                            );
                        case CertificateType.InternationalCertificateForOperatorsOfPleasureCraft:
                            return (
                                <InternationalCertificateView
                                    certificate={certificate}
                                    idDataSet={idDataSet}
                                    modifyable={modifyable}
                                />
                            );
                        case CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS:
                            return (
                                <InternationalCertificateView
                                    certificate={certificate}
                                    idDataSet={idDataSet}
                                    modifyable={modifyable && !isUpdate}
                                />
                            );
                        case CertificateType.CertificateForSailingOnStretchesOfWaterwaysThatPresentSpecificRisksForNavigationAt:
                            return (
                                <SailingOnWaterwaysCertificateView
                                    certificate={certificate}
                                    idDataSet={idDataSet}
                                    modifyable={modifyable}
                                />
                            );
                        default:
                            return (
                                <div className="default-certificate-view">
                                    <h3>{intl.formatMessage({ id: "noViewAvailable" })}</h3>
                                </div>
                            );
                    }
                })()}
            </Modal.Content>
            {modifyable && (
                <Modal.Actions>
                    <Button
                        negative
                        onClick={close}
                        content={intl.formatMessage({ id: "cancel" })}
                        disabled={loading}
                    />
                    {!readonly && (
                        <Button
                            primary
                            onClick={() => {
                                if (isUpdate) {
                                    handleUpdateCertificate();
                                } else if (renew) {
                                    handleRenewCertificate();
                                } else {
                                    handleAddCertificate();
                                }
                            }}
                            content={
                                certificate.type ===
                                CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS
                                    ? intl.formatMessage({ id: "saveChanges" })
                                    : intl.formatMessage({ id: "publish" })
                            }
                            loading={loading}
                            disabled={loading}
                        />
                    )}
                </Modal.Actions>
            )}
        </Modal>
    );
};
